import * as THREE from 'three';
import { SVGLoader, StrokeStyle } from 'three/addons/loaders/SVGLoader.js';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';

const INSTANCED_MATERIALS = [
  new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide
  }),
  new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide
  }),
  new THREE.MeshBasicMaterial({
    color: 0x000000,
    side: THREE.DoubleSide
  })
];

// 定义精度配置
const GEOMETRY_CONFIG = {
  curveSegments: 6, // 默认是 12
  lineDivisions: 6, // 取样点数，默认是 12
  simplifyThreshold: 0.125, // 像素单位，小于这个距离的点会被合并
  strokeWidth: 0.5 // 线条宽度（用于 pointsToStroke）
};

// 定义处理后的数据结构
interface IFurnitureAssetData {
  originWidth: number;
  originHeight: number;
  offsetX: number;
  offsetY: number;
  // 用于 InstancedMesh 的合并几何体
  mergedGeometry: THREE.BufferGeometry | null;
}

export class FurnitureAssets {
  private static cache = new Map<string, IFurnitureAssetData>();
  private static loader = new SVGLoader();

  // InstancedMesh 使用的材质数组
  public static get instancedMaterials() {
    return INSTANCED_MATERIALS;
  }

  public static getAsset(svgContent: string): IFurnitureAssetData {
    const cached = this.cache.get(svgContent);
    if (cached) {
      return cached;
    }

    const asset = this.processSVG(svgContent);
    this.cache.set(svgContent, asset);
    return asset;
  }

  private static processSVG(svgContent: string): IFurnitureAssetData {
    const data = this.loader.parse(svgContent);
    const paths = data.paths;
    const viewBox = (data.xml as unknown as SVGSVGElement).viewBox.baseVal;
    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = (
      (data.xml as unknown as SVGSVGElement).dataset?.padding || '0 0 0 0'
    )
      .trim()
      .split(/[\s]+/)
      .map(Number);

    const originWidth = viewBox.width - paddingRight - paddingLeft;
    const originHeight = viewBox.height - paddingTop - paddingBottom;

    const offsetX = -(originWidth / 2 + paddingLeft);
    const offsetY = originHeight / 2 + paddingTop;

    const blackShapes: THREE.ShapeGeometry[] = [];
    const whiteShapes: THREE.ShapeGeometry[] = [];
    const strokeMeshes: THREE.BufferGeometry[] = []; // 用于 InstancedMesh

    // 线条样式（用于 pointsToStroke）
    // getStrokeStyle 参数: (width, color, lineJoin, lineCap, miterLimit)
    const strokeStyle: StrokeStyle = SVGLoader.getStrokeStyle(
      GEOMETRY_CONFIG.strokeWidth,
      '#000000',
      'miter',
      'butt',
      4
    );

    for (const path of paths) {
      const fillColor = path.userData?.style?.fill;
      const fillOpacity = path.userData?.style?.fillOpacity ?? 1;

      if (fillColor && fillColor !== 'none' && fillOpacity !== 0) {
        const shapes = SVGLoader.createShapes(path);

        for (const shape of shapes) {
          // 控制 ShapeGeometry 的精度，减少三角形数量
          const geom = new THREE.ShapeGeometry(shape, GEOMETRY_CONFIG.curveSegments);
          // 判断是否为白色填充
          if (fillColor === '#fff') {
            whiteShapes.push(geom);
          } else {
            // 剩下的全当黑色处理
            blackShapes.push(geom);
          }
        }
      }

      const strokeColor = path.userData?.style?.stroke;
      const strokeOpacity = path.userData?.style?.strokeOpacity ?? 1;

      if (strokeColor && strokeColor !== 'none' && strokeOpacity !== 0) {
        for (const subPath of path.subPaths) {
          // 控制线条采样的精度
          let points = subPath.getPoints(GEOMETRY_CONFIG.lineDivisions);

          // 简单的点云抽稀算法，减少冗余点
          points = this.simplifyPoints(points, GEOMETRY_CONFIG.simplifyThreshold);

          // 用于 InstancedMesh：将线条转为 Mesh
          const strokeGeometry = SVGLoader.pointsToStroke(points, strokeStyle);
          if (strokeGeometry) {
            strokeMeshes.push(strokeGeometry);
          }
        }
      }
    }

    // --- 创建合并的几何体（用于 InstancedMesh）---
    let mergedGeometry: THREE.BufferGeometry | null = null;
    const geometriesToMerge: THREE.BufferGeometry[] = [];
    const groupInfo: { count: number; materialIndex: number }[] = [];

    // 白色层 (materialIndex = 0)
    if (whiteShapes.length > 0) {
      const whiteGmt = BufferGeometryUtils.mergeGeometries(whiteShapes);
      if (whiteGmt) {
        const nonIndexed = whiteGmt.index ? whiteGmt.toNonIndexed() : whiteGmt;
        const count = nonIndexed.attributes.position.count;
        geometriesToMerge.push(nonIndexed);
        groupInfo.push({ count, materialIndex: 0 });
      }
    }

    // 黑色层 (materialIndex = 1)
    if (blackShapes.length > 0) {
      const blackGmt = BufferGeometryUtils.mergeGeometries(blackShapes);
      if (blackGmt) {
        const nonIndexed = blackGmt.index ? blackGmt.toNonIndexed() : blackGmt;
        const count = nonIndexed.attributes.position.count;
        geometriesToMerge.push(nonIndexed);
        groupInfo.push({ count, materialIndex: 1 });
      }
    }

    // 线条层 (materialIndex = 2) - 现在也是 Mesh
    if (strokeMeshes.length > 0) {
      const strokeGmt = BufferGeometryUtils.mergeGeometries(strokeMeshes);
      if (strokeGmt) {
        const nonIndexed = strokeGmt.index ? strokeGmt.toNonIndexed() : strokeGmt;
        const count = nonIndexed.attributes.position.count;
        geometriesToMerge.push(nonIndexed);
        groupInfo.push({ count, materialIndex: 2 });
      }
      strokeMeshes.forEach((g) => g.dispose());
    }

    // 最终合并
    if (geometriesToMerge.length > 0) {
      mergedGeometry = BufferGeometryUtils.mergeGeometries(geometriesToMerge);
      if (mergedGeometry) {
        mergedGeometry.clearGroups();
        let currentStart = 0;
        for (const { count, materialIndex } of groupInfo) {
          mergedGeometry.addGroup(currentStart, count, materialIndex);
          currentStart += count;
        }
      }
    }

    // 清理临时几何体
    blackShapes.forEach((g) => g.dispose());
    whiteShapes.forEach((g) => g.dispose());

    return {
      originWidth,
      originHeight,
      offsetX,
      offsetY,
      mergedGeometry
    };
  }

  public static clearCache() {
    this.cache.forEach((asset) => {
      asset.mergedGeometry?.dispose();
    });
    this.cache.clear();
  }

  /**
   * 简单的点云抽稀算法
   * @param points 原始点数组
   * @param threshold 距离阈值，小于此距离的点会被合并
   * @returns 简化后的点数组
   */
  private static simplifyPoints(points: THREE.Vector2[], threshold: number): THREE.Vector2[] {
    if (points.length < 3) return points;

    const result = [points[0]];
    let lastPoint = points[0];
    const thresholdSq = threshold * threshold;

    for (let i = 1; i < points.length - 1; i++) {
      const dist = lastPoint.distanceToSquared(points[i]);
      // 只有距离大于阈值，才保留
      if (dist > thresholdSq) {
        result.push(points[i]);
        lastPoint = points[i];
      }
    }

    // 确保闭合形状依然闭合
    const originalLast = points[points.length - 1];
    const newLast = result[result.length - 1];
    if (!newLast.equals(originalLast)) {
      result.push(originalLast);
    }

    return result;
  }
}
